import React, { useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import { formatCardTitle, getNextCardDraftLabel, moveCard } from '~/utils/societePayloadBuilder/whatsNewCarousel/cards';
import { createDefaultCard } from '~/utils/societePayloadBuilder/whatsNewCarousel/defaults';
import {
  WHATS_NEW_CAROUSEL_ENABLED_REGIONS_HELPER_TEXT,
  WHATS_NEW_CAROUSEL_OPTIMIZELY_FEATURE_FLAG_HELPER_TEXT,
  fieldLabel,
} from '~/utils/societePayloadBuilder/whatsNewCarousel/fieldLabels';
import type { WhatsNewCarouselCard } from '~/utils/societePayloadBuilder/whatsNewCarousel/types';
import { WHATS_NEW_CAROUSEL_IMAGE_FIT_OPTIONS } from '~/utils/societePayloadBuilder/whatsNewCarousel/types';

import { PayloadBuilderImageIdField, PayloadBuilderTextField } from '../FormFields';
import PayloadBuilderRegionChipsField from '../RegionChipsField';
import { SectionCard } from '../SectionCard';

import { WhatsNewCarouselCtaUrlField } from './WhatsNewCarouselCtaUrlField';

interface WhatsNewCarouselCardsSectionProps {
  cards: Array<WhatsNewCarouselCard>;
  onChange: (cards: Array<WhatsNewCarouselCard>) => void;
}

function updateCard(
  cards: Array<WhatsNewCarouselCard>,
  index: number,
  patch: Partial<WhatsNewCarouselCard>,
): Array<WhatsNewCarouselCard> {
  return cards.map((card, cardIndex) => (cardIndex === index ? { ...card, ...patch } : card));
}

interface WhatsNewCarouselCardEditorProps {
  card: WhatsNewCarouselCard;
  index: number;
  cards: Array<WhatsNewCarouselCard>;
  onChange: (cards: Array<WhatsNewCarouselCard>) => void;
}

function WhatsNewCarouselCardEditor({ card, index, cards, onChange }: WhatsNewCarouselCardEditorProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const title = formatCardTitle(card);

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${borderSoft}`, borderRadius: 1, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          bgcolor: alpha(theme.palette.primary.main, expanded ? 0.08 : 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: expanded ? `1px solid ${borderSoft}` : 'none',
        }}
      >
        <Button
          variant="text"
          color="primary"
          onClick={() => setExpanded((value) => !value)}
          endIcon={
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
              }}
            />
          }
          sx={{
            minWidth: 0,
            justifyContent: 'flex-start',
            px: 0.5,
            textTransform: 'none',
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          <Typography variant="subtitle2" component="span" noWrap>
            {title}
          </Typography>
        </Button>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          <Chip label={`Position ${index + 1}`} size="small" color="primary" variant="outlined" />
          <Tooltip title="Move card up">
            <Box component="span">
              <IconButton
                aria-label={`Move card ${index + 1} up`}
                disabled={index === 0}
                onClick={() => onChange(moveCard(cards, index, -1))}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Move card down">
            <Box component="span">
              <IconButton
                aria-label={`Move card ${index + 1} down`}
                disabled={index === cards.length - 1}
                onClick={() => onChange(moveCard(cards, index, 1))}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Button
            size="small"
            variant={card.isActive ? 'contained' : 'outlined'}
            color={card.isActive ? 'success' : 'inherit'}
            onClick={() => onChange(updateCard(cards, index, { isActive: !card.isActive }))}
          >
            {card.isActive ? 'Active' : 'Inactive'}
          </Button>
          <Tooltip title="Remove card">
            <IconButton
              aria-label={`Remove card ${index + 1}`}
              onClick={() => onChange(cards.filter((_, cardIndex) => cardIndex !== index))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderRegionChipsField
                label={fieldLabel('enabledRegions')}
                helperText={WHATS_NEW_CAROUSEL_ENABLED_REGIONS_HELPER_TEXT}
                regions={card.enabledRegions}
                onChange={(enabledRegions) => onChange(updateCard(cards, index, { enabledRegions }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('startAt')}
                type="datetime-local"
                value={card.startAt}
                onChange={(event) => onChange(updateCard(cards, index, { startAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('endAt')}
                type="datetime-local"
                value={card.endAt}
                onChange={(event) => onChange(updateCard(cards, index, { endAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderImageIdField
                label={fieldLabel('imageId')}
                required
                value={card.imageId}
                onChange={(imageId) => onChange(updateCard(cards, index, { imageId }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel id={`whats-new-carousel-image-fit-${card.draftId}`}>
                  {fieldLabel('imageOptionsFit')}
                </InputLabel>
                <Select
                  labelId={`whats-new-carousel-image-fit-${card.draftId}`}
                  label={fieldLabel('imageOptionsFit')}
                  value={card.imageOptionsFit}
                  onChange={(event) =>
                    onChange(
                      updateCard(cards, index, {
                        imageOptionsFit: event.target.value as WhatsNewCarouselCard['imageOptionsFit'],
                      }),
                    )
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {WHATS_NEW_CAROUSEL_IMAGE_FIT_OPTIONS.map((fitOption) => (
                    <MenuItem key={fitOption} value={fitOption}>
                      {fitOption}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('optimizelyFeatureFlag')}
                value={card.optimizelyFeatureFlag}
                onChange={(event) => onChange(updateCard(cards, index, { optimizelyFeatureFlag: event.target.value }))}
                helperText={WHATS_NEW_CAROUSEL_OPTIMIZELY_FEATURE_FLAG_HELPER_TEXT}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('overline')}
                value={card.overline}
                onChange={(event) => onChange(updateCard(cards, index, { overline: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('title')}
                value={card.title}
                onChange={(event) => onChange(updateCard(cards, index, { title: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderTextField
                label={fieldLabel('description')}
                value={card.description}
                onChange={(event) => onChange(updateCard(cards, index, { description: event.target.value }))}
                required
                multiline
                minRows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('ctaLabel')}
                value={card.ctaLabel}
                onChange={(event) => onChange(updateCard(cards, index, { ctaLabel: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <WhatsNewCarouselCtaUrlField
                value={card.ctaUrl}
                onChange={(event) => onChange(updateCard(cards, index, { ctaUrl: event.target.value }))}
                required
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function WhatsNewCarouselCardsSection({ cards, onChange }: WhatsNewCarouselCardsSectionProps) {
  return (
    <SectionCard title="Carousel cards" subtitle="Cards shown in the Société What's New carousel." count={cards.length}>
      <Stack spacing={2}>
        {cards.map((card, index) => (
          <WhatsNewCarouselCardEditor key={card.draftId} card={card} index={index} cards={cards} onChange={onChange} />
        ))}
        <Button
          variant="outlined"
          onClick={() => onChange([...cards, createDefaultCard(getNextCardDraftLabel(cards))])}
        >
          Add card
        </Button>
      </Stack>
    </SectionCard>
  );
}
